/** Mail view preferences — Settings → Mail (`localStorage` + same-tab events). */

import type { MailMessage } from "@/lib/api";

export const MAIL_VIEW_KEY = "atw-hub-mail-view";
export const MAIL_CONVERSATIONS_KEY = "atw-hub-mail-conversations";
export const MAIL_THREAD_ORDER_KEY = "atw-hub-mail-thread-order";
export const MAIL_SWIPE_LEFT_KEY = "atw-hub-mail-swipe-left";
export const MAIL_SWIPE_RIGHT_KEY = "atw-hub-mail-swipe-right";
export const MAIL_CLOCK_FORMAT_KEY = "atw-hub-mail-clock-format";
export const MAIL_LINE_SPACING_KEY = "atw-hub-mail-line-spacing";

export const MAIL_VIEW_EVENT = "atw-hub-mail-view";
export const MAIL_CONVERSATIONS_EVENT = "atw-hub-mail-conversations";
export const MAIL_THREAD_ORDER_EVENT = "atw-hub-mail-thread-order";
export const MAIL_SWIPE_EVENT = "atw-hub-mail-swipe";
export const MAIL_CLOCK_FORMAT_EVENT = "atw-hub-mail-clock-format";
export const MAIL_LINE_SPACING_EVENT = "atw-hub-mail-line-spacing";

export const MAIL_VIEW_OPTIONS = [
  { id: "tree", label: "Folder Tree" },
  { id: "switcher", label: "Account Switcher" },
  { id: "chips", label: "Chips" },
] as const;

export const MAIL_THREAD_ORDER_OPTIONS = [
  { id: "newest-first", label: "Newest First" },
  { id: "oldest-first", label: "Oldest First" },
] as const;

/** List row drag/swipe actions (desktop pointer + future touch list). */
export const MAIL_SWIPE_ACTIONS = [
  { id: "none", label: "Nothing" },
  { id: "toggle-read", label: "Mark Read / Unread" },
  { id: "read", label: "Mark Read" },
  { id: "unread", label: "Mark Unread" },
  { id: "archive", label: "Archive" },
  { id: "delete", label: "Delete" },
  { id: "star", label: "Star / Unstar" },
] as const;

/** List/reader clock: 12-hour (AM/PM) or 24-hour. */
export const MAIL_CLOCK_FORMAT_OPTIONS = [
  { id: "12h", label: "12-hour (AM/PM)" },
  { id: "24h", label: "24-hour" },
] as const;

export type MailViewId = (typeof MAIL_VIEW_OPTIONS)[number]["id"];
export type MailThreadOrderId = (typeof MAIL_THREAD_ORDER_OPTIONS)[number]["id"];
export type MailSwipeActionId = (typeof MAIL_SWIPE_ACTIONS)[number]["id"];
export type MailClockFormatId = (typeof MAIL_CLOCK_FORMAT_OPTIONS)[number]["id"];

/** Compose and message line spacing. Standard is the default. */
export const MAIL_LINE_SPACING_OPTIONS = [
  { id: "compact", label: "Compact", lineHeight: "1.15" },
  { id: "standard", label: "Standard", lineHeight: "1.35" },
  { id: "relaxed", label: "Relaxed", lineHeight: "1.7" },
] as const;

export type MailLineSpacingId = (typeof MAIL_LINE_SPACING_OPTIONS)[number]["id"];

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function readMailView(): MailViewId {
  const raw = readRaw(MAIL_VIEW_KEY);
  if (raw === "switcher" || raw === "chips" || raw === "tree") return raw;
  return "tree";
}

export function readMailConversations(): boolean {
  return readRaw(MAIL_CONVERSATIONS_KEY) !== "off";
}

export function readMailThreadOrder(): MailThreadOrderId {
  const raw = readRaw(MAIL_THREAD_ORDER_KEY);
  if (raw === "oldest-first") return "oldest-first";
  return "newest-first";
}

export function writeMailView(value: MailViewId) {
  try {
    localStorage.setItem(MAIL_VIEW_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_VIEW_EVENT, { detail: value }));
}

export function writeMailConversations(on: boolean) {
  try {
    localStorage.setItem(MAIL_CONVERSATIONS_KEY, on ? "on" : "off");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_CONVERSATIONS_EVENT, { detail: on ? "on" : "off" }));
}

export function writeMailThreadOrder(value: MailThreadOrderId) {
  try {
    localStorage.setItem(MAIL_THREAD_ORDER_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_THREAD_ORDER_EVENT, { detail: value }));
}

function isSwipeAction(value: string | null): value is MailSwipeActionId {
  return MAIL_SWIPE_ACTIONS.some((action) => action.id === value);
}

/** Default: swipe left → read/unread toggle. */
export function readMailSwipeLeft(): MailSwipeActionId {
  const raw = readRaw(MAIL_SWIPE_LEFT_KEY);
  if (isSwipeAction(raw)) return raw;
  return "toggle-read";
}

/** Default: swipe right → archive. */
export function readMailSwipeRight(): MailSwipeActionId {
  const raw = readRaw(MAIL_SWIPE_RIGHT_KEY);
  if (isSwipeAction(raw)) return raw;
  return "archive";
}

export function writeMailSwipeLeft(value: MailSwipeActionId) {
  try {
    localStorage.setItem(MAIL_SWIPE_LEFT_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_SWIPE_EVENT, { detail: { side: "left", value } }));
}

export function writeMailSwipeRight(value: MailSwipeActionId) {
  try {
    localStorage.setItem(MAIL_SWIPE_RIGHT_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_SWIPE_EVENT, { detail: { side: "right", value } }));
}

export function mailSwipeActionLabel(id: MailSwipeActionId): string {
  return MAIL_SWIPE_ACTIONS.find((action) => action.id === id)?.label || "Nothing";
}

/** Default: 12-hour clock with AM/PM. */
export function readMailClockFormat(): MailClockFormatId {
  const raw = readRaw(MAIL_CLOCK_FORMAT_KEY);
  if (raw === "24h" || raw === "12h") return raw;
  return "12h";
}

export function writeMailClockFormat(value: MailClockFormatId) {
  try {
    localStorage.setItem(MAIL_CLOCK_FORMAT_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_CLOCK_FORMAT_EVENT, { detail: value }));
}

/** Default is Standard, the same spacing as a normal email. */
export function readMailLineSpacing(): MailLineSpacingId {
  const raw = readRaw(MAIL_LINE_SPACING_KEY);
  if (raw === "compact" || raw === "standard" || raw === "relaxed") return raw;
  return "standard";
}

export function mailLineSpacingLineHeight(spacing: MailLineSpacingId = readMailLineSpacing()): string {
  return MAIL_LINE_SPACING_OPTIONS.find((option) => option.id === spacing)?.lineHeight || "1.35";
}

export function writeMailLineSpacing(value: MailLineSpacingId) {
  try {
    localStorage.setItem(MAIL_LINE_SPACING_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(MAIL_LINE_SPACING_EVENT, { detail: value }));
}

/** Put the chosen line height on the sent HTML so other mail apps keep it. */
export function applyMailLineSpacing(html: string, spacing: MailLineSpacingId = readMailLineSpacing()): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;
  const lineHeight = mailLineSpacingLineHeight(spacing);
  if (trimmed.includes("data-hub-spacing=")) {
    return trimmed
      .replace(/data-hub-spacing="(?:compact|standard|relaxed)"/, `data-hub-spacing="${spacing}"`)
      .replace(/line-height:\s*[\d.]+/, `line-height:${lineHeight}`);
  }
  return `<div data-hub-spacing="${spacing}" style="line-height:${lineHeight}">${trimmed}</div>`;
}

/** Sort messages inside an open conversation per Thread Order setting. */
export function orderMailThreadMessages(
  messages: MailMessage[],
  order: MailThreadOrderId = readMailThreadOrder(),
): MailMessage[] {
  const list = [...messages];
  list.sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return order === "oldest-first" ? da - db : db - da;
  });
  return list;
}

/** Newest message id for collapse-on-open / newest-only reader. */
export function newestMailMessageId(messages: MailMessage[]): string | null {
  if (!messages.length) return null;
  const ordered = orderMailThreadMessages(messages, "newest-first");
  const first = ordered[0];
  return first?.id != null ? String(first.id) : null;
}

/** Default accordion: newest open, older bodies collapsed (multi-message only). */
export function defaultCollapsedBodies(messages: MailMessage[]): Record<string, boolean> {
  const keepOpen = newestMailMessageId(messages);
  const next: Record<string, boolean> = {};
  if (messages.length <= 1 || !keepOpen) return next;
  for (const message of messages) {
    const key = String(message.id || "");
    if (key && key !== keepOpen) next[key] = true;
  }
  return next;
}

/**
 * Keep the reader's accordion toggles across detail refreshes.
 * Only collapses brand-new older message ids; never re-opens the newest or
 * undoes a manual expand/collapse.
 */
export function mergeCollapsedBodies(
  prev: Record<string, boolean>,
  messages: MailMessage[],
): Record<string, boolean> {
  if (messages.length <= 1) return {};
  const keepOpen = newestMailMessageId(messages);
  const known = new Set(
    messages.map((message) => String(message.id || "")).filter(Boolean),
  );
  const next: Record<string, boolean> = {};
  for (const [key, collapsed] of Object.entries(prev)) {
    if (known.has(key)) next[key] = collapsed;
  }
  for (const message of messages) {
    const key = String(message.id || "");
    if (!key || key in next) continue;
    if (key !== keepOpen) next[key] = true;
  }
  return next;
}
